import { useState, useMemo, useRef, useEffect } from "react";
import useIsMobile from "../client/hooks/useIsMobile";
import { useParams, useNavigate } from "react-router-dom";
import { getCustomPageById, applyLayoutToCustomPage, useQuery, useAction } from "wasp/client/operations";
import { DashboardDataProvider } from "../dashboard/widgets/DashboardDataContext";
import DashboardLayout from "../dashboard/widgets/DashboardLayout";
import EditModeBar from "../dashboard/widgets/EditModeBar";
import AddWidgetPicker, { TOPBAR_WIDGET_KEYS } from "../dashboard/widgets/AddWidgetPicker";
import ResetLayoutDialog from "../dashboard/widgets/ResetLayoutDialog";
import { useActiveLayout, compactSlots } from "../dashboard/widgets/useActiveLayout";
import { Button } from "../client/components/ui/button";
import { Plus, Layers, ChevronDown } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { isMissingResource } from "./queryErrorStatus";
import { PageTransition } from "../client/components/ui/motion";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "../dashboard/widgets/types";
import { useCustomPageData } from "./useCustomPageData";
import type { DateRange } from "../client/utils/dateRange";
import type { AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import AddTradeModal from "../trading-journal/AddTradeModal";
import AddPayoutModal from "../prop-firm-manager/AddPayoutModal";
import {
  getDefaultDateRange,
  getDefaultAccountFilter,
  getDefaultStrategyIds,
  getDefaultPfmFilter,
  getDefaultPhaseFilter,
  serializeDateRange,
  serializeAccountFilter,
  serializeStrategyIds,
  serializePhaseFilter,
} from "./topbarDefaults";
import { useToast } from "../client/hooks/use-toast";
import { SortableTopbarSlot, TopbarDndWrapper } from "../dashboard/widgets/TopbarSlotRenderer";
import TopbarFilterBar from "../dashboard/widgets/TopbarFilterBar";
import LayoutLibraryModal from "../dashboard/widgets/LayoutLibraryModal";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";

function layoutFromRows(rows: any[]): LayoutSnapshot {
  const allSlots: SlotPlacement[] = rows.flatMap((row: any) =>
    (row.slots ?? []).map((slot: any) => ({
      id: slot.id,
      widgetKey: slot.widgetKey as WidgetKey,
      colStart: slot.colStart,
      rowStart: row.order + slot.rowStart - 1,
      colSpan: slot.colSpan,
      rowSpan: slot.rowSpan,
      notebookNoteId: slot.notebookNoteId ?? undefined,
      placement: (slot.placement as "grid" | "topbar") ?? undefined,
      defaultFilterValue: slot.defaultFilterValue ?? undefined,
      viewMode: (slot.viewMode as "chart" | "table") ?? undefined,
    }))
  );
  const gridSlots = allSlots.filter((s) => s.placement !== "topbar");
  const topbarSlots = allSlots.filter((s) => s.placement === "topbar");
  return { slots: [...topbarSlots, ...compactSlots(gridSlots)] };
}

// ---------------------------------------------------------------------------
// CustomPageView
// ---------------------------------------------------------------------------

export default function CustomPageView() {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const t = useTranslation();
  const {
    data: page,
    isLoading,
    error: pageError,
    refetch: refetchPage,
  } = useQuery(getCustomPageById, { pageId: pageId! });
  const applyLayoutFn = useAction(applyLayoutToCustomPage);

  const [appliedLayoutId, setAppliedLayoutId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<{
    colStart: number;
    rowStart: number;
    maxColSpan: number;
    context: "topbar" | "grid";
  } | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const serverSnapshot: LayoutSnapshot = useMemo(() => {
    if (!page?.layout) return { slots: [] };
    return layoutFromRows((page.layout as any).rows ?? []);
  }, [page]);

  const isLayoutReadOnly = !!(page?.layout as any)?.isReadOnly;
  const layoutHook = useActiveLayout(serverSnapshot, page?.layoutId ?? null, undefined, isLayoutReadOnly);

  // Topbar filter state — initialized from saved defaults
  const [dateRange, setDateRange] = useState<DateRange>(
    () => getDefaultDateRange(serverSnapshot.slots)
  );
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>(
    () => getDefaultAccountFilter(serverSnapshot.slots)
  );
  const [strategyIds, setStrategyIds] = useState<string[]>(
    () => getDefaultStrategyIds(serverSnapshot.slots)
  );
  const [pfmTemplateFilter, setPfmTemplateFilter] = useState<string | null>(
    () => getDefaultPfmFilter(serverSnapshot.slots)
  );
  const [accountStatusFilter, setAccountStatusFilter] = useState<string | null>(
    () => getDefaultPhaseFilter(serverSnapshot.slots)
  );
  const [addTradeOpen, setAddTradeOpen] = useState(false);
  const [addPayoutOpen, setAddPayoutOpen] = useState(false);
  const [layoutLibraryOpen, setLayoutLibraryOpen] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const { toast } = useToast();

  const filterInitialized = useRef<string | null>(null);
  useEffect(() => {
    if (filterInitialized.current === pageId || !page) return;
    filterInitialized.current = pageId!;
    setDateRange(getDefaultDateRange(serverSnapshot.slots));
    setAccountFilter(getDefaultAccountFilter(serverSnapshot.slots));
    setStrategyIds(getDefaultStrategyIds(serverSnapshot.slots));
    setPfmTemplateFilter(getDefaultPfmFilter(serverSnapshot.slots));
    setAccountStatusFilter(getDefaultPhaseFilter(serverSnapshot.slots));
  }, [pageId, page, serverSnapshot]);

  const pageData = useCustomPageData({ dateRange, accountFilter, strategyIds, pfmTemplateFilter, setPfmTemplateFilter, accountStatusFilter, setAccountStatusFilter });
  const widgetData = { ...pageData, onOpenPlanModal: () => setShowPlanModal(true), showPlanModal, setShowPlanModal };

  const topbarSlots = useMemo(
    () => layoutHook.layout.slots.filter((s) => s.placement === "topbar").sort((a, b) => a.colStart - b.colStart),
    [layoutHook.layout.slots]
  );

  const isEmpty = layoutHook.layout.slots.filter((s) => s.placement !== "topbar").length === 0;

  // Doit rester AVANT les early returns ci-dessous : c'est un hook.
  const isMobile = useIsMobile();

  // Page supprimée ou appartenant à un autre utilisateur : on redirige.
  // Impérativement dans un effet — l'ancien code appelait navigate() PENDANT le
  // rendu, ce qui déclenche "Cannot update a component while rendering a
  // different component" et peut interrompre le commit React en cours.
  const shouldRedirect =
    !isLoading && (isMissingResource(pageError) || (!page && !pageError));
  useEffect(() => {
    if (shouldRedirect) navigate("/dashboard", { replace: true });
  }, [shouldRedirect, navigate]);

  if (isLoading) return null;

  // Erreur serveur ou réseau : la page existe probablement toujours, on ne
  // redirige pas — l'utilisateur peut réessayer sans perdre son contexte.
  if (pageError && !isMissingResource(pageError)) {
    return (
      <PageTransition>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <p className="text-foreground mb-1 text-base font-medium">
            {t.pages.loadErrorTitle}
          </p>
          <p className="text-muted-foreground mb-4 text-sm">
            {t.pages.loadErrorDescription}
          </p>
          <Button variant="outline" size="sm" onClick={() => refetchPage()}>
            {t.pages.loadErrorRetry}
          </Button>
        </div>
      </PageTransition>
    );
  }

  // Page supprimée (404) ou appartenant à quelqu'un d'autre (403) : la
  // redirection est déclenchée par l'effet ci-dessus, on ne rend rien ici.
  if (!page) return null;

  const extraTopbarActions = (
    <>
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 gap-1 max-w-[140px]"
        onClick={() => setLayoutLibraryOpen(true)}
      >
        <Layers className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{page?.name ?? t.dashboard.layouts.menuTitle}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>
      {!isEditMode && (
        <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
          {t.pages.edit}
        </Button>
      )}
    </>
  );

  return (
    <PageTransition className="w-full">
      <DashboardDataProvider value={widgetData}>
        <div className="w-full py-6 space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <h1 className="text-2xl font-bold">{page.name}</h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {isEditMode ? (
                <TopbarDndWrapper
                  slots={topbarSlots}
                  onSwap={(idA, idB) => { layoutHook.swapWidgets(idA, idB); layoutHook.flushSave(); }}
                >
                  {topbarSlots.map((slot) => (
                    <SortableTopbarSlot
                      key={slot.id}
                      id={slot.id}
                      slot={slot}
                      isEditMode={true}
                      dateRange={dateRange}
                      accountFilter={accountFilter}
                      strategyIds={strategyIds}
                      accounts={pageData.accounts}
                      allAccounts={pageData.allAccounts}
                      groups={pageData.groups}
                      strategies={pageData.strategies}
                      onDateRangeChange={setDateRange}
                      onAccountFilterChange={setAccountFilter}
                      onStrategyIdsChange={setStrategyIds}
                      onAddTrade={() => setAddTradeOpen(true)}
                      onAddPayout={() => setAddPayoutOpen(true)}
                      onRemove={() => layoutHook.removeWidget(slot.id)}
                      onPin={isLayoutReadOnly ? undefined : (value) => {
                        layoutHook.updateSlotDefaultFilterValue(slot.id, value);
                        layoutHook.flushSave();
                        toast({ description: value === null ? t.pages.unpinDefault : t.pages.pinAsDefaultConfirm, duration: 1500 });
                      }}
                      pfmTemplateFilter={pfmTemplateFilter}
                      onPfmTemplateFilterChange={setPfmTemplateFilter}
                      accountStatusFilter={accountStatusFilter}
                      onAccountStatusFilterChange={setAccountStatusFilter}
                    />
                  ))}
                </TopbarDndWrapper>
              ) : (
                <TopbarFilterBar
                  slots={topbarSlots}
                  dateRange={dateRange}
                  accountFilter={accountFilter}
                  strategyIds={strategyIds}
                  accounts={pageData.accounts}
                  allAccounts={pageData.allAccounts}
                  groups={pageData.groups}
                  strategies={pageData.strategies}
                  onDateRangeChange={setDateRange}
                  onAccountFilterChange={setAccountFilter}
                  onStrategyIdsChange={setStrategyIds}
                  onAddTrade={() => setAddTradeOpen(true)}
                  onAddPayout={() => setAddPayoutOpen(true)}
                  onRemove={() => {}}
                  onPin={undefined}
                  pfmTemplateFilter={pfmTemplateFilter}
                  onPfmTemplateFilterChange={setPfmTemplateFilter}
                  accountStatusFilter={accountStatusFilter}
                  onAccountStatusFilterChange={setAccountStatusFilter}
                  extraActions={extraTopbarActions}
                />
              )}
              {isEditMode && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs h-8"
                  onClick={() => {
                    setPickerTarget({ colStart: 1, rowStart: 1, maxColSpan: 6, context: "topbar" });
                    setPickerOpen(true);
                  }}
                >
                  {t.pages.topbarAdd}
                </Button>
              )}
              <LayoutLibraryModal
                open={layoutLibraryOpen}
                onOpenChange={setLayoutLibraryOpen}
                scope="custom-page"
                isFreePlan={pageData.isFreePlan}
                onUpgradeClick={() => setShowPlanModal(true)}
                onBeforeActivate={async () => {
                  await layoutHook.flushSave();
                }}
                activeLayoutId={appliedLayoutId}
                pageLayout={{ id: page.layoutId, name: page.name, shareToken: (page.layout as any)?.shareToken ?? null }}
                onActivate={async (layoutId) => {
                  const result = await (applyLayoutFn as any)({ pageId: pageId!, sourceLayoutId: layoutId });
                  if (result?.rows) {
                    layoutHook.resetToServerState(layoutFromRows(result.rows));
                    setAppliedLayoutId(layoutId);
                  }
                }}
              />
            </div>
          </div>

          {isEditMode && (
            <EditModeBar
              onReset={() => setResetOpen(true)}
              onDone={() => {
                layoutHook.flushSave();
                setIsEditMode(false);
              }}
            />
          )}

          {isEmpty && !isEditMode ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-muted-foreground">
              <p className="text-lg">{t.pages.emptyPage}</p>
              <Button onClick={() => setIsEditMode(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t.pages.addFirstWidget}
              </Button>
            </div>
          ) : (
            <DashboardLayout
              layout={layoutHook.layout}
              isEditMode={isEditMode}
              isMobile={isMobile}
              onAddWidgetClick={(colStart, rowStart, maxColSpan) => {
                setPickerTarget({ colStart, rowStart, maxColSpan, context: "grid" });
                setPickerOpen(true);
              }}
              onRemoveWidget={layoutHook.removeWidget}
              onWidgetSizeChange={layoutHook.setWidgetSize}
              onMoveWidget={layoutHook.moveWidget}
              onSwapWidgets={layoutHook.swapWidgets}
              onUpdateSlotConfig={layoutHook.updateSlotDefaultFilterValue}
              onUpdateSlotViewMode={layoutHook.updateSlotViewMode}
            />
          )}

          {pickerTarget && (
            <AddWidgetPicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              layout={layoutHook.layout}
              maxColSpan={pickerTarget.maxColSpan}
              rowStart={pickerTarget.rowStart}
              colStart={pickerTarget.colStart}
              scope={pickerTarget.context === "topbar" ? "custom-page" : "dashboard"}
              isFreePlan={pageData.isFreePlan}
              onUpgradeClick={() => setShowPlanModal(true)}
              onPick={(widgetKey) => {
                if (TOPBAR_WIDGET_KEYS.has(widgetKey)) {
                  layoutHook.addWidget(widgetKey, 1, 0, 1, 1, "topbar", null);
                } else {
                  layoutHook.addWidget(widgetKey, pickerTarget.colStart, pickerTarget.rowStart);
                }
                setPickerOpen(false);
                setPickerTarget(null);
              }}
              topbarPinState={pickerTarget.context === "topbar" ? {
                values: {
                  "topbar-date-filter": serializeDateRange(dateRange),
                  "topbar-account-filter": serializeAccountFilter(accountFilter),
                  "topbar-strategy-filter": serializeStrategyIds(strategyIds),
                },
                defaults: Object.fromEntries(
                  topbarSlots.map((s) => [s.widgetKey, s.defaultFilterValue ?? null])
                ) as Partial<Record<import("../dashboard/widgets/types").WidgetKey, string | null>>,
                onPin: (widgetKey, value) => {
                  const slot = topbarSlots.find((s) => s.widgetKey === widgetKey);
                  if (!slot) return;
                  layoutHook.updateSlotDefaultFilterValue(slot.id, value);
                  layoutHook.flushSave();
                  toast({ description: t.pages.pinAsDefaultConfirm, duration: 1500 });
                },
              } : undefined}
            />
          )}

          <ResetLayoutDialog
            open={resetOpen}
            onOpenChange={setResetOpen}
            onConfirm={async () => {
              await layoutHook.compactAndFlushSave();
              setResetOpen(false);
            }}
          />

          <AddTradeModal
            open={addTradeOpen}
            onOpenChange={setAddTradeOpen}
            onImportDone={() => {}}
          />
          <AddPayoutModal
            open={addPayoutOpen}
            onClose={() => setAddPayoutOpen(false)}
            accounts={pageData.accounts.map((a: any) => ({
              id: a.id,
              name: a.name,
              status: a.status ?? "active",
            }))}
            onSuccess={() => setAddPayoutOpen(false)}
          />
          <PlanSelectionModal
            open={showPlanModal}
            onOpenChange={setShowPlanModal}
          />
        </div>
      </DashboardDataProvider>
    </PageTransition>
  );
}
